/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
import { MetaData, ServiceLineInfo } from './claims.model';

import {
  Attachment,
  ServiceLineDetails,
  ReconsiderationType,
} from '@cigna/chcp/shared/util';

export interface UserBase {
  id: string;
  firstName: string;
  lastName: string;
}

export enum AdjustmentStatus {
  ADJUSTED = 'ADJUSTED',
  CLOSED = 'CLOSED',
  CLOSED_REROUTED_APPEAL = 'CLOSED_REROUTED_APPEAL',
  CLOSED_REROUTED_RECON = 'CLOSED_REROUTED_RECON',
  DRAFT = 'DRAFT',
  DOWNCODING_UNDER_REVIEW = 'DOWNCODING_UNDER_REVIEW',
  DOWNCODING_UPHELD = 'DOWNCODING_UPHELD',
  IN_PROCESS = 'IN_PROCESS',
  NOT_ADJUSTED_BANKING_RUNOUT = 'NOT_ADJUSTED_BANKING_RUNOUT',
  NOT_ADJUSTED_COB = 'NOT_ADJUSTED_COB',
  NOT_ADJUSTED_ELIGIBILITY = 'NOT_ADJUSTED_ELIGIBILITY',
  NOT_ADJUSTED_NOT_WORKED = 'NOT_ADJUSTED_NOT_WORKED',
  NOT_ADJUSTED_TIMELY_FILING = 'NOT_ADJUSTED_TIMELY_FILING',
  OVERTURNED = 'OVERTURNED',
  OVERTURN_REVERTED = 'OVERTURN_REVERTED',
  PARTIAL_OVERTURN = 'PARTIAL_OVERTURN',
  PENDED = 'PENDED',
  PROCESSING_ERROR = 'PROCESSING_ERROR',
  RECEIVED = 'RECEIVED',
  REROUTED_TO_CORR = 'REROUTED_TO_CORR',
  REVIEW_REQUIRED = 'REVIEW_REQUIRED',
  SCANNER_FAILED = 'SCANNER_FAILED',
  SCANNER_PENDING = 'SCANNER_PENDING',
  UNAVAILABLE = 'UNAVAILABLE',
  UPHELD_INSUFFICIENT_INFO = 'UPHELD_INSUFFICIENT_INFO',
  UPHELD_NONTIMELY_FILING = 'UPHELD_NONTIMELY_FILING', // todo: confirm with andrew we can remove this
  UPHELD_REVERTED = 'UPHELD_REVERTED',
  UPHELD_SUCCESS = 'UPHELD_SUCCESS',
}

export const activeAdjustmentStatuses: AdjustmentStatusType[] = [
  AdjustmentStatus.DRAFT,
  AdjustmentStatus.UNAVAILABLE,
  AdjustmentStatus.IN_PROCESS,
  AdjustmentStatus.RECEIVED,
  AdjustmentStatus.PENDED,
  AdjustmentStatus.SCANNER_PENDING,
];

// TODO: rename to AdjustmentDisplayStatus
// const should have key of AdjustmentStatus and value of string for display purposes
export const adjustmentDisplayStatusNEW: Record<AdjustmentStatus, string> = {
  [AdjustmentStatus.ADJUSTED]: 'Adjusted',
  [AdjustmentStatus.CLOSED]: 'Closed',
  [AdjustmentStatus.CLOSED_REROUTED_APPEAL]: 'Closed. Re-routed to Appeals',
  [AdjustmentStatus.CLOSED_REROUTED_RECON]:
    'Closed. Re-routed to Reconsiderations',
  [AdjustmentStatus.DOWNCODING_UNDER_REVIEW]: 'In Process',
  [AdjustmentStatus.DOWNCODING_UPHELD]: 'Review Upheld',
  [AdjustmentStatus.DRAFT]: 'Draft',
  [AdjustmentStatus.IN_PROCESS]: 'In Process',
  [AdjustmentStatus.OVERTURN_REVERTED]: 'Review Upheld',
  [AdjustmentStatus.OVERTURNED]: 'Overturned',
  [AdjustmentStatus.PARTIAL_OVERTURN]: 'Partially Overturned',
  [AdjustmentStatus.PENDED]: 'Pended',
  [AdjustmentStatus.PROCESSING_ERROR]:
    'Request failed – Help desk notified – Please resubmit in 48 hours',
  [AdjustmentStatus.RECEIVED]: 'Received',
  [AdjustmentStatus.REROUTED_TO_CORR]: 'In Progress – additional review',
  [AdjustmentStatus.REVIEW_REQUIRED]:
    'Additional review required. Resolution response to follow',
  [AdjustmentStatus.SCANNER_FAILED]: 'Request Failed - Virus Found',
  [AdjustmentStatus.SCANNER_PENDING]: 'Pending Virus Scan',
  [AdjustmentStatus.UNAVAILABLE]: 'Status Unavailable - Check Back Later',
  [AdjustmentStatus.UPHELD_INSUFFICIENT_INFO]:
    'Claim Processing Upheld - Insufficient Information Provided',
  [AdjustmentStatus.UPHELD_NONTIMELY_FILING]: 'Not Reviewed - Timely Filing',
  [AdjustmentStatus.UPHELD_REVERTED]: 'Overturned',
  [AdjustmentStatus.UPHELD_SUCCESS]:
    'Claim Processing Upheld - Claim Processed Correctly',
  [AdjustmentStatus.NOT_ADJUSTED_NOT_WORKED]: 'Not Adjusted - Not Reviewed',
  [AdjustmentStatus.NOT_ADJUSTED_COB]: 'Not Adjusted - EOB Needed',
  [AdjustmentStatus.NOT_ADJUSTED_ELIGIBILITY]: 'Not Adjusted - Eligibility',
  [AdjustmentStatus.NOT_ADJUSTED_BANKING_RUNOUT]: 'Not Adjusted - Banking',
  [AdjustmentStatus.NOT_ADJUSTED_TIMELY_FILING]: 'Not Reviewed - Timely Filing',
};

export const decisionNotesDowncoding: Partial<
  Record<AdjustmentStatus, string>
> = {
  RECEIVED: 'Not yet available',
  DOWNCODING_UNDER_REVIEW: 'Not yet available',
  IN_PROCESS: 'Not yet available',
  NOT_ADJUSTED_BANKING_RUNOUT:
    'At this time, were unable to make any adjustments to this claim due to a banking-related issue.',
  NOT_ADJUSTED_COB:
    "This reconsideration was not adjusted because coordination of benefits information is incomplete. Please submit the primary insurer's Explanation of Benefits (EOB) and resubmit your reconsideration for review.",
  NOT_ADJUSTED_ELIGIBILITY:
    "This reconsideration was not adjusted because the member was not eligible for coverage on the date of service. If you believe eligibility information has changed or was incorrect, please verify the member's coverage and submit a new reconsideration with supporting documentation.",
  NOT_ADJUSTED_TIMELY_FILING:
    'Reconsideration not reviewed due to timely filing requirements. Clinical review was not completed.',
  OVERTURNED:
    'The review team looked at everything and decided to change the original decision. A payment adjustment will be applied to the claim and reflected on an updated remittance report.',
  UPHELD_REVERTED:
    'The review team looked at everything and decided to change the original decision. A payment adjustment will be applied to the claim and reflected on an updated remittance report.',
  DOWNCODING_UPHELD:
    'After reviewing the submitted medical records, we agree with our original decision.',
  UPHELD_NONTIMELY_FILING:
    'Reconsideration not reviewed due to timely filing requirements . Clinical review was not completed.',
  UPHELD_SUCCESS:
    'After reviewing the submitted medical records, we agree with our original decision.',
  UPHELD_INSUFFICIENT_INFO:
    'After reviewing the submitted medical records, we agree with our original decision.',
  OVERTURN_REVERTED:
    'After reviewing the submitted medical records, we agree with our original decision.',
  PARTIAL_OVERTURN:
    'Our team changed part of the original decision, but some parts stayed the same. If your claim had more than one date of service, not all procedure codes were updated during the review.',
};

export const knownDowncodeStatuses: AdjustmentStatus[] = [
  AdjustmentStatus.DOWNCODING_UNDER_REVIEW,
  AdjustmentStatus.IN_PROCESS,
  AdjustmentStatus.RECEIVED,

  AdjustmentStatus.PARTIAL_OVERTURN,
  AdjustmentStatus.DOWNCODING_UPHELD,

  AdjustmentStatus.OVERTURNED,
  AdjustmentStatus.OVERTURN_REVERTED,
  AdjustmentStatus.UPHELD_REVERTED,

  AdjustmentStatus.NOT_ADJUSTED_COB,
  AdjustmentStatus.NOT_ADJUSTED_ELIGIBILITY,
  AdjustmentStatus.NOT_ADJUSTED_BANKING_RUNOUT,
  AdjustmentStatus.NOT_ADJUSTED_TIMELY_FILING,
  AdjustmentStatus.NOT_ADJUSTED_NOT_WORKED,
];

export interface ClaimsAdjustmentsResponse {
  metadata: MetaData;
  adjustmentsHistory: Adjustment[];
}

export type AdjustmentStatusType = keyof typeof AdjustmentStatus;

export type AppealPathType = 'Participant' | 'Provider';

export interface Adjustment {
  appealsReason?: string;
  attachments: string[];
  attachmentsData: Attachment[];
  attachmentsRequired?: boolean;
  choiceOne: number | null;
  choiceThree: number | null;
  choiceTwo: number | null;
  claimDos: string;
  claimReferenceNumber?: string;
  confirmationAnswer?: string;
  confirmationQuestion?: string;
  contactInformation: ContactInfoAdjustment;
  coverageType: string;
  createDate?: string;
  createdBy?: UserBase;
  decisionDate?: string;
  decisionNotes?: string;
  expirationTime: number | null;
  extRefNum: string;
  groupNumber: string;
  householdSuffix: string;
  id: string;
  isDowncodingAdjustment?: boolean;
  memberDob: string;
  memberFirstName: string;
  memberId: string;
  memberLastName: string;
  nonTimelyAttachmentsRequired?: boolean;
  nonTimelyChoice: number | null;
  nonTimelyNotesRequired?: boolean;
  notes: string;
  notesRequired?: boolean;
  oneViewRequestType?: string;
  OvUpdated?: string;
  placeOfService?: string;
  precertId: string;
  procedureLines?: ServiceLineInfo[];
  processStateUrl: string;
  providerId: string;
  providerName: string;
  providerRenderingState?: string;
  reconsiderationType?: ReconsiderationType | '';
  requesterName: string;
  requesterSSOId: string;
  requestType?: string;
  reviewType: ReviewType;
  serviceLines?: ServiceLineDetails[];
  sourceSystemCode: string;
  stateOfService: string;
  status: AdjustmentStatusType;
  submittedBy?: UserBase;
  submittedDate?: string;
  tinNumber?: string;
  typeOfInitiator?: AppealPathType;
  typeOfNetwork: string;
  updateDate?: string;
  updatedBy?: UserBase;
}

export interface MemberGroupSummary {
  idCardExtension: string;
  groupNumber: string;
  ami: string;
}

export interface MemberGroupRequest {
  memberFirstName: string;
  relationshipCode: string;
  memberLastName: string;
  memberDateOfBirth: string;
  memberId: string;
  coverageType: string;
  eligibilityAsOfDate: string;
  memberIdentifier: string;
}

interface ContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
}
export interface ContactInfoAdjustment extends ContactInfo {
  phone: string;
}

export interface ContactInfoFormData extends ContactInfo {
  phone: PhoneData;
}

export interface PhoneData {
  phone1: string;
  phone2: string;
  phone3: string;
}

export type ReviewType = 'Pre-Service' | 'Post-Service';
